import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Statistic, Table, Tag, Avatar, Empty, Spin, Alert, Button, Progress, Divider, Tooltip, Badge } from 'antd'
import {
  PlayCircleOutlined,
  AppstoreOutlined,
  UserOutlined,
  TrophyOutlined,
  FireOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  LikeOutlined,
  StarOutlined,
  ReloadOutlined,
  TagsOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileAddOutlined,
  GlobalOutlined,
  BarChartOutlined,
  RiseOutlined,
  GoldOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons'
import { adminAPI } from '../services/adminAPI'
import { gamesAPI, categoriesAPI, authAPI } from '../services/api'
import type { ColumnsType } from 'antd/es/table'

interface DashboardOverview {
  totalGames: number
  approvedGames: number
  pendingGames: number
  draftGames: number
  totalCategories: number
  totalTags: number
  totalUsers: number
  activeUsers: number
  totalCreators: number
  totalSessions: number
  monthlyPlays: number
  totalPlayCount: number
  avgPlaysPerGame: number
  totalLikes: number
  avgRating: number
  featuredGames: number
}

interface TopGame {
  id: string
  name: string
  slug: string
  iconUrl?: string
  playCount: number
  likeCount: number
  rating: number
  status?: string
  developer?: string
}

interface TopUser {
  id: string
  username: string
  displayName?: string
  avatarUrl?: string
  totalPlaysReceived: number
  createdGamesCount: number
}

interface TopActiveUser {
  userId: string
  username: string
  displayName?: string
  avatarUrl?: string
  sessionCount: number
  lastActive?: string
}

interface GameByStatus {
  status: string
  count: number
}

interface ActivityItem {
  id: string
  type?: 'play' | 'game_created' | 'user_registered' | 'game_approved'
  gameId?: string
  gameName?: string
  userId?: string
  username: string
  action?: string
  createdAt: string
}

interface DashboardData {
  overview: DashboardOverview
  topGames: TopGame[]
  topUsers: TopUser[]
  topActiveUsers: TopActiveUser[]
  gamesByStatus: GameByStatus[]
  recentActivity: ActivityItem[]
}

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'green',
  PENDING: 'orange',
  DRAFT: 'blue',
  REJECTED: 'red',
  ARCHIVED: 'default',
}

const STATUS_LABEL: Record<string, string> = {
  APPROVED: 'Approved',
  PENDING: 'Pending',
  DRAFT: 'Draft',
  REJECTED: 'Rejected',
  ARCHIVED: 'Archived',
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Try the dashboard stats endpoint first
      try {
        const response = await adminAPI.getDashboardStats()
        if (response.success && response.data) {
          // Ensure backward-compatibility: if old API response, enrich overview defaults
          const data = response.data
          if (!data.overview.approvedGames) {
            // Calculate from gamesByStatus if not provided
            const statusMap: Record<string, number> = {}
            ;(data.gamesByStatus || []).forEach((s: GameByStatus) => {
              statusMap[s.status] = s.count
            })
            ;(data.overview as any).approvedGames = statusMap['APPROVED'] || 0
            ;(data.overview as any).pendingGames = statusMap['PENDING'] || 0
            ;(data.overview as any).draftGames = statusMap['DRAFT'] || 0
          }
          if (!data.overview.totalTags) (data.overview as any).totalTags = 0
          if (!data.overview.totalCreators) (data.overview as any).totalCreators = 0
          if (!data.overview.featuredGames) (data.overview as any).featuredGames = 0
          if (!data.overview.totalLikes) (data.overview as any).totalLikes = 0
          if (!data.overview.avgRating) (data.overview as any).avgRating = 0
          if (!data.overview.avgPlaysPerGame) (data.overview as any).avgPlaysPerGame = 0
          setDashboardData(data as DashboardData)
          setLastRefreshed(new Date())
          return
        }
      } catch (dashboardErr: any) {
        console.warn('Dashboard stats endpoint not available, using fallback:', dashboardErr.message)
      }

      // Fallback: Load data from multiple legacy endpoints
      const [gamesResponse, categoriesResponse, usersResponse] = await Promise.all([
        gamesAPI.getAllForAdmin(),
        categoriesAPI.getAll(),
        authAPI.getAllUsers(),
      ])

      const games = gamesResponse.data || []
      const users = usersResponse.data || []

      const totalPlayCount = games.reduce((sum: number, game: any) => sum + (game.playCount || 0), 0)
      const totalLikes = games.reduce((sum: number, game: any) => sum + (game.likeCount || 0), 0)
      const activeUsers = users.filter((user: any) => user.isActive).length

      const topGames = [...games]
        .sort((a: any, b: any) => (b.playCount || 0) - (a.playCount || 0))
        .slice(0, 10)
        .map((game: any) => ({
          id: game.id,
          name: game.name,
          slug: game.slug,
          iconUrl: game.iconUrl,
          playCount: game.playCount || 0,
          likeCount: game.likeCount || 0,
          rating: game.rating || 0,
          status: game.status,
          developer: game.developer,
        }))

      const statusCounts: Record<string, number> = {}
      games.forEach((game: any) => {
        const status = game.status || 'DRAFT'
        statusCounts[status] = (statusCounts[status] || 0) + 1
      })
      const gamesByStatus = Object.entries(statusCounts).map(([status, count]) => ({ status, count }))

      setDashboardData({
        overview: {
          totalGames: games.length,
          approvedGames: statusCounts['APPROVED'] || 0,
          pendingGames: statusCounts['PENDING'] || 0,
          draftGames: statusCounts['DRAFT'] || 0,
          totalCategories: categoriesResponse.data?.length || 0,
          totalTags: 0,
          totalUsers: users.length,
          activeUsers,
          totalCreators: 0,
          totalSessions: 0,
          monthlyPlays: 0,
          totalPlayCount,
          avgPlaysPerGame: games.length > 0 ? Math.round(totalPlayCount / games.length) : 0,
          totalLikes,
          avgRating: 0,
          featuredGames: games.filter((g: any) => g.isFeatured).length,
        },
        topGames,
        topUsers: [],
        topActiveUsers: [],
        gamesByStatus,
        recentActivity: [],
      })
      setLastRefreshed(new Date())
    } catch (err: any) {
      console.error('Error loading dashboard data:', err)
      setError(err.message || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const formatDate = (dateString: string): string => {
    if (!dateString) return '—'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '—'
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString('vi-VN')
  }

  const getActivityIcon = (type?: string) => {
    switch (type) {
      case 'play': return <PlayCircleOutlined style={{ color: '#1890ff' }} />
      case 'game_created': return <PlusCircleOutlined style={{ color: '#52c41a' }} />
      case 'game_approved': return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'user_registered': return <UserOutlined style={{ color: '#722ed1' }} />
      default: return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
    }
  }

  const getActivityText = (activity: ActivityItem): string => {
    switch (activity.type) {
      case 'play':
        return `${activity.username} đã chơi ${activity.gameName || 'Unknown Game'}`
      case 'game_created':
        return `Game "${activity.gameName}" được ${activity.action === 'published' ? 'xuất bản' : 'thêm vào'} bởi ${activity.username}`
      case 'game_approved':
        return `Game "${activity.gameName}" được phê duyệt`
      case 'user_registered':
        return `${activity.username} vừa đăng ký tài khoản`
      default:
        return `${activity.username} ${activity.action || 'interacted'} với ${activity.gameName || ''}`
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#8c8c8c' }}>Đang tải dữ liệu dashboard...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Lỗi tải Dashboard"
        description={error}
        type="error"
        showIcon
        action={
          <Button type="primary" danger onClick={loadDashboardData} style={{ marginLeft: 16 }}>
            Thử lại
          </Button>
        }
      />
    )
  }

  if (!dashboardData) {
    return <Empty description="Không có dữ liệu dashboard" />
  }

  const { overview, topGames, topUsers, topActiveUsers, gamesByStatus, recentActivity } = dashboardData

  // ─── Table: Top Played Games ───────────────────────────────────────────────
  const topGamesColumns: ColumnsType<TopGame> = [
    {
      title: '#',
      key: 'rank',
      width: 48,
      render: (_: any, __: any, index: number) => (
        <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'default'}>
          {index + 1}
        </Tag>
      ),
    },
    {
      title: 'Game',
      key: 'game',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.iconUrl ? (
            <Avatar src={record.iconUrl} shape="square" size={36} style={{ flexShrink: 0 }} />
          ) : (
            <Avatar shape="square" size={36} icon={<PlayCircleOutlined />} />
          )}
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{record.name}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>/{record.slug}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => status ? (
        <Tag color={STATUS_COLOR[status] || 'default'} style={{ fontSize: 11 }}>
          {STATUS_LABEL[status] || status}
        </Tag>
      ) : null,
    },
    {
      title: 'Lượt chơi',
      dataIndex: 'playCount',
      key: 'playCount',
      width: 100,
      sorter: (a, b) => a.playCount - b.playCount,
      defaultSortOrder: 'descend',
      render: (count: number) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>{formatNumber(count)}</span>
      ),
    },
    {
      title: 'Likes',
      dataIndex: 'likeCount',
      key: 'likeCount',
      width: 80,
      sorter: (a, b) => a.likeCount - b.likeCount,
      render: (count: number) => (
        <span style={{ color: '#eb2f96' }}>
          <LikeOutlined style={{ marginRight: 4 }} />{formatNumber(count)}
        </span>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 80,
      sorter: (a, b) => a.rating - b.rating,
      render: (rating: number) => (
        <span>
          <StarOutlined style={{ color: '#faad14', marginRight: 4 }} />
          {Number(rating).toFixed(1)}
        </span>
      ),
    },
  ]

  // ─── Table: Top Creators ───────────────────────────────────────────────────
  const topUsersColumns: ColumnsType<TopUser> = [
    {
      title: '#',
      key: 'rank',
      width: 48,
      render: (_: any, __: any, index: number) => (
        <Tag color={index < 3 ? 'gold' : 'default'}>{index + 1}</Tag>
      ),
    },
    {
      title: 'Creator',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar src={record.avatarUrl} icon={<UserOutlined />} size={32} />
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>
              {record.displayName || record.username}
            </div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>@{record.username}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Games',
      dataIndex: 'createdGamesCount',
      key: 'createdGamesCount',
      width: 70,
      render: (count: number) => (
        <Tag color="blue">{count}</Tag>
      ),
    },
    {
      title: 'Lượt chơi nhận được',
      dataIndex: 'totalPlaysReceived',
      key: 'totalPlaysReceived',
      sorter: (a, b) => a.totalPlaysReceived - b.totalPlaysReceived,
      defaultSortOrder: 'descend',
      render: (count: number) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>{formatNumber(count)}</span>
      ),
    },
  ]

  // ─── Table: Most Active Players ────────────────────────────────────────────
  const topActiveUsersColumns: ColumnsType<TopActiveUser> = [
    {
      title: '#',
      key: 'rank',
      width: 48,
      render: (_: any, __: any, index: number) => (
        <Tag color={index < 3 ? 'gold' : 'default'}>{index + 1}</Tag>
      ),
    },
    {
      title: 'Người chơi',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar src={record.avatarUrl} icon={<UserOutlined />} size={32} />
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>
              {record.displayName || record.username}
            </div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>@{record.username}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      sorter: (a, b) => a.sessionCount - b.sessionCount,
      defaultSortOrder: 'descend',
      render: (count: number) => (
        <span style={{ fontWeight: 600, color: '#722ed1' }}>{formatNumber(count)}</span>
      ),
    },
    {
      title: 'Hoạt động cuối',
      dataIndex: 'lastActive',
      key: 'lastActive',
      render: (date: string) => date ? (
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{formatDate(date)}</span>
      ) : '—',
    },
  ]

  // Approve rate
  const approvalRate = overview.totalGames > 0
    ? Math.round((overview.approvedGames / overview.totalGames) * 100)
    : 0

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Dashboard</h1>
          {lastRefreshed && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              Cập nhật lúc: {lastRefreshed.toLocaleTimeString('vi-VN')}
            </div>
          )}
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadDashboardData}
          loading={loading}
          type="primary"
          ghost
        >
          Làm mới
        </Button>
      </div>

      {/* ── Row 1: Tổng quan Games & Content ─────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tổng Games"
              value={overview.totalGames}
              prefix={<PlayCircleOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Đã duyệt"
              value={overview.approvedGames}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Chờ duyệt"
              value={overview.pendingGames}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: overview.pendingGames > 0 ? '#faad14' : '#8c8c8c', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Nổi bật"
              value={overview.featuredGames}
              prefix={<GoldOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Categories"
              value={overview.totalCategories}
              prefix={<AppstoreOutlined style={{ color: '#13c2c2' }} />}
              valueStyle={{ color: '#13c2c2', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tags"
              value={overview.totalTags}
              prefix={<TagsOutlined style={{ color: '#eb2f96' }} />}
              valueStyle={{ color: '#eb2f96', fontSize: 22 }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Row 2: Users & Sessions ───────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tổng Users"
              value={overview.totalUsers}
              prefix={<UserOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1', fontSize: 22 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
              {overview.activeUsers} đang hoạt động
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Users Active"
              value={overview.activeUsers}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: 22 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
              {overview.totalUsers > 0
                ? `${((overview.activeUsers / overview.totalUsers) * 100).toFixed(1)}% tổng số`
                : '0%'}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Creators"
              value={overview.totalCreators}
              prefix={<GlobalOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tổng Sessions"
              value={overview.totalSessions}
              prefix={<ClockCircleOutlined style={{ color: '#fa541c' }} />}
              valueStyle={{ color: '#fa541c', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Plays 30 ngày"
              value={overview.monthlyPlays}
              prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tổng Lượt Chơi"
              value={formatNumber(overview.totalPlayCount)}
              prefix={<FireOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d', fontSize: 22 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
              ~{formatNumber(overview.avgPlaysPerGame)}/game TB
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Row 3: Engagement & Quality ──────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Tổng Lượt Thích"
              value={formatNumber(overview.totalLikes)}
              prefix={<LikeOutlined style={{ color: '#eb2f96' }} />}
              valueStyle={{ color: '#eb2f96', fontSize: 22 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <Statistic
              title="Rating TB"
              value={Number(overview.avgRating).toFixed(1)}
              prefix={<StarOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14', fontSize: 22 }}
              suffix="/ 5"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>Tỷ lệ phê duyệt</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: '#1890ff', marginBottom: 6 }}>
              {approvalRate}%
            </div>
            <Progress
              percent={approvalRate}
              strokeColor="#52c41a"
              trailColor="#f0f0f0"
              size="small"
              showInfo={false}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
              {overview.approvedGames} / {overview.totalGames} games
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>Phân bổ trạng thái</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {gamesByStatus.map((item) => (
                <Tooltip key={item.status} title={`${item.count} games`}>
                  <Tag color={STATUS_COLOR[item.status] || 'default'} style={{ marginBottom: 4 }}>
                    {STATUS_LABEL[item.status] || item.status}: {item.count}
                  </Tag>
                </Tooltip>
              ))}
              {gamesByStatus.length === 0 && (
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>Không có dữ liệu</span>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Top Played Games ─────────────────────────────────────────────── */}
      <Card
        title={
          <span>
            <FireOutlined style={{ marginRight: 8, color: '#f5222d' }} />
            Top Games Được Chơi Nhiều Nhất
          </span>
        }
        style={{ marginBottom: 24 }}
        extra={
          <Tag color="blue">{topGames.length} games</Tag>
        }
      >
        {topGames.length > 0 ? (
          <Table
            columns={topGamesColumns}
            dataSource={topGames}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 600 }}
          />
        ) : (
          <Empty description="Chưa có game nào được chơi" />
        )}
      </Card>

      {/* ── Top Creators & Active Players ────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <TrophyOutlined style={{ marginRight: 8, color: '#faad14' }} />
                Top Creators (Lượt Chơi Nhận Được)
              </span>
            }
            extra={<Tag color="gold">{topUsers.length} creators</Tag>}
          >
            {topUsers.length > 0 ? (
              <Table
                columns={topUsersColumns}
                dataSource={topUsers}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 400 }}
              />
            ) : (
              <Empty description="Chưa có dữ liệu creator" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <TeamOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                Người Chơi Tích Cực Nhất
              </span>
            }
            extra={<Tag color="purple">{topActiveUsers.length} players</Tag>}
          >
            {topActiveUsers.length > 0 ? (
              <Table
                columns={topActiveUsersColumns}
                dataSource={topActiveUsers}
                rowKey="userId"
                pagination={false}
                size="small"
                scroll={{ x: 400 }}
              />
            ) : (
              <Empty description="Chưa có dữ liệu người chơi" />
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Recent Activity ───────────────────────────────────────────────── */}
      <Card
        title={
          <span>
            <ClockCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Hoạt Động Gần Đây
          </span>
        }
        extra={
          <Tag color="blue">{recentActivity.length} events</Tag>
        }
      >
        {recentActivity.length > 0 ? (
          <div>
            {recentActivity.map((activity, index) => (
              <div
                key={activity.id}
                style={{
                  padding: '10px 0',
                  borderBottom: index < recentActivity.length - 1 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <div style={{ marginTop: 2, flexShrink: 0 }}>
                  {getActivityIcon(activity.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {getActivityText(activity)}
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    {formatDate(activity.createdAt)}
                    {activity.type && (
                      <Tag
                        style={{ marginLeft: 8, fontSize: 10 }}
                        color={
                          activity.type === 'play' ? 'blue'
                            : activity.type === 'game_created' ? 'green'
                            : activity.type === 'user_registered' ? 'purple'
                            : 'default'
                        }
                      >
                        {activity.type === 'play' ? 'Chơi game'
                          : activity.type === 'game_created' ? 'Thêm game'
                          : activity.type === 'user_registered' ? 'Đăng ký'
                          : activity.type}
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="Chưa có hoạt động nào được ghi nhận" />
        )}
      </Card>
    </div>
  )
}

export default Dashboard
